import React, { ReactElement, useEffect } from "react";
import { GameState } from "../types";
import { rulesFor } from "./rules";
import "./RulesSheet.css";

type RulesSheetProps = {
  gameState: GameState;
  onClose: () => void;
};

/** A one-screen reminder of the rules for this table size, in a window over the game. */
export default function RulesSheet(props: RulesSheetProps): ReactElement {
  const game = props.gameState;
  const count = game.playerOrder.length;
  const rules = rulesFor(count);
  const enacted = game.fascistPolicies ?? 0;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [props]);

  return (
    <div className="rs-layer">
      <div className="rs-backdrop" onClick={props.onClose} />
      <div className="rs-panel" role="dialog" aria-modal="true" aria-label="Rules reminder">
        <div className="rs-head">
          <h2 className="rs-title">Rules for {count} players</h2>
          <button className="rs-close" aria-label="Close" onClick={props.onClose}>
            ×
          </button>
        </div>

        <section className="rs-section">
          <h3>The teams</h3>
          <p>
            <strong className="rs-liberal">{rules.liberals} Liberals</strong>,{" "}
            <strong className="rs-fascist">
              {rules.fascists} {rules.fascists === 1 ? "Fascist" : "Fascists"}
            </strong>{" "}
            and <strong className="rs-hitler">Hitler</strong> (who counts as a Fascist). The Fascists know each other.{" "}
            {rules.hitlerKnowsFascists
              ? "Hitler knows who the Fascist is."
              : "Hitler does not know who the Fascists are."}
          </p>
        </section>

        <section className="rs-section">
          <h3>The Fascist track: what each policy unlocks</h3>
          <div className="rs-track">
            {rules.track.map((slot, i) => {
              const done = i < enacted;
              return (
                <div
                  key={i}
                  className={
                    "rs-slot" + (slot.none ? " rs-slot-none" : "") + (i === 5 ? " rs-slot-win" : "") + (done ? " rs-slot-done" : "")
                  }
                >
                  <div className="rs-slot-n">{i + 1}</div>
                  <div className="rs-slot-title">{slot.title}</div>
                  <div className="rs-slot-text">{slot.text}</div>
                  {done && <div className="rs-slot-badge">Enacted</div>}
                </div>
              );
            })}
          </div>
          <p className="rs-note">The power happens right after the policy is enacted, and only for Fascist policies.</p>
        </section>

        <section className="rs-section">
          <h3>Winning</h3>
          <ul>
            <li>
              <strong className="rs-liberal">Liberals:</strong> enact 5 Liberal policies, or execute Hitler.
            </li>
            <li>
              <strong className="rs-fascist">Fascists:</strong> enact 6 Fascist policies, or elect Hitler as Chancellor
              after 3 Fascist policies are in.
            </li>
          </ul>
        </section>

        <section className="rs-section">
          <h3>Every turn</h3>
          <ul>
            <li>The President nominates a Chancellor, and everyone votes Ja or Nein.</li>
            <li>The last elected Chancellor cannot be nominated again, and neither can the last elected President (unless only 5 players are alive).</li>
            <li>Three failed votes in a row enact the top policy and reset the tracker.</li>
            <li>The President draws three policies and discards one; the Chancellor enacts one of the other two.</li>
          </ul>
        </section>
      </div>
    </div>
  );
}
